import {useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import noop from 'lodash/noop'

import {useOnClickOutside} from '@vio/atlas/hooks/useOnClickOutside'
import {isFlutterAppWebView} from '@vio/core/src/_web/utils/flutter/isFlutterAppWebView'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  TrackEventPayload
} from '@vio/core/src/analytics/types/Events'

import {useOsanoDetection} from './useOsanoDetection'
import {
  CookieBannerClickComponent,
  getCookieConsentComponent,
  getCookieConsentContext,
  storeCookieConsentTimestamp
} from './utils'

export interface Props {
  /**
   * Callback invoked when a user accepts the cookies, either in the
   * banner or the preference center
   */
  onAccept?: (acceptedByUser: boolean) => void
  trackEvent: (payload: TrackEventPayload) => void
  withBannerBackground?: boolean
}

export const useOsanoCookieConsent = ({
  onAccept: setCookiesAcceptedByUser = noop,
  trackEvent,
  withBannerBackground = false
}: Props) => {
  const {locale} = useIntl()
  const {isLoaded, isOpened, dialogRef} = useOsanoDetection()

  const isEnabled = !isFlutterAppWebView() && isOpened
  const [backgroundVisible, setBackgroundVisible] = useState(false)

  const trackBannerSubmitted = useCallback(
    (component?: CookieBannerClickComponent) => {
      // The cookie consent id is not updated immediately when the user clicks on the accept button
      // so we need to wait for it to be updated in localStorage
      setTimeout(() => {
        const cookieConsentContext = getCookieConsentContext()

        trackEvent?.({
          category: Category.User,
          entity: Entity.CookieBanner,
          action: Action.Submitted,
          analyticsContext: {
            [AnalyticsContext.CookieConsentContext]: cookieConsentContext
          },
          component
        })
      }, 500)
    },
    [trackEvent]
  )

  const trackBannerShown = useCallback(() => {
    const cookieConsentContext = getCookieConsentContext()

    trackEvent?.({
      category: Category.System,
      entity: Entity.CookieBanner,
      action: Action.Displayed,
      analyticsContext: {
        [AnalyticsContext.CookieConsentContext]: cookieConsentContext
      }
    })
  }, [trackEvent])

  const onAccept = useCallback(() => {
    storeCookieConsentTimestamp()
    setCookiesAcceptedByUser(true)
  }, [setCookiesAcceptedByUser])

  const onDeny = useCallback(() => {
    setCookiesAcceptedByUser(false)
  }, [setCookiesAcceptedByUser])

  const handleOsanoConsentSaved = useCallback(
    consent => {
      if (window?.Osano?.cm?.drawerOpen) {
        trackBannerSubmitted(CookieBannerClickComponent.PreferenceCenter)
        onAccept()
      } else if (window?.Osano?.cm?.dialogOpen) {
        const component = getCookieConsentComponent(consent)
        if (component === CookieBannerClickComponent.AcceptButton) {
          onAccept()
        } else {
          onDeny()
        }

        trackBannerSubmitted(component)
      }
      setBackgroundVisible(false)
    },
    [trackBannerSubmitted, onAccept, onDeny]
  )

  // Track the cookie consent script loaded event from Osano
  useEffect(() => {
    if (isLoaded) {
      trackEvent({
        category: Category.System,
        entity: Entity.CookieBannerScript,
        action: Action.Loaded
      })
    }
  }, [trackEvent, isLoaded])

  // Deny all cookies when the user clicks outside the cookie banner
  useOnClickOutside(dialogRef, () => {
    if (window?.Osano?.cm?.dialogOpen && !withBannerBackground) {
      const denyAllButton = document.querySelector(
        '.osano-cm-denyAll'
      ) as HTMLButtonElement

      // In some cases, the deny all button is not available, so the banner will not be closed.
      // Checking if the button is available prevents from triggering the AppPage event multiple times
      if (denyAllButton) {
        denyAllButton.click()
        trackBannerSubmitted(CookieBannerClickComponent.AppPage)
      }
    }
  })

  // Track the cookie banner shown event from Osano
  useEffect(() => {
    if (isEnabled) trackBannerShown()
  }, [trackBannerShown, isEnabled])

  // Set the locale for the cookie banner
  useEffect(() => {
    const sanitizedLocale = locale.split('-')[0]
    if (isLoaded && window.Osano?.cm) {
      window.Osano.cm.locale = sanitizedLocale
    }
  }, [locale, isLoaded])

  // Display the cookie banner
  useEffect(() => {
    if (isEnabled && dialogRef.current) {
      dialogRef.current.style.display = 'flex'
      setBackgroundVisible(withBannerBackground)
    }
  }, [isEnabled, dialogRef])

  // Track the consent saved event from Osano when the user clicks on the accept/reject buttons
  // it is triggered when the user clicks on the accept button in the preference center
  useEffect(() => {
    window?.Osano?.cm?.addEventListener(
      'osano-cm-consent-saved',
      handleOsanoConsentSaved
    )

    return () => {
      window?.Osano?.cm?.removeEventListener(
        'osano-cm-consent-saved',
        handleOsanoConsentSaved
      )
    }
  }, [handleOsanoConsentSaved])

  return {backgroundVisible}
}
